import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { REACTION_EMOJIS } from '@lessalone/shared';
import { BlockModal } from '../components/BlockModal';
import { GoodbyeLetter } from '../components/GoodbyeLetter';
import { CrisisResources } from '../components/CrisisResources';
import { PeerAgreementModal } from '../components/PeerAgreementModal';
import { ReportModal } from '../components/ReportModal';
import {
  acceptPairingAgreement,
  addReaction,
  blockConversation,
  fetchConversation,
  fetchMessages,
  removeReaction,
  reportConversation,
  sendMessage,
  type ChatMessage,
  type ConversationDetail,
  type MeResponse,
  type MessageReaction,
} from '../lib/api';
import { hapticTap } from '../lib/capacitor';

/**
 * The async 1:1 chat view. Messages are polled — there is no live socket.
 *
 * Optimistic send: the user's message appears immediately on submit with
 * a `pending` flag, then merges with the server's confirmed copy on the
 * next refresh. A failed send is shown in place with a Retry affordance
 * so the user never wonders whether their message went through.
 *
 * On mobile, the view is full-bleed (the .card chrome collapses) and the
 * composer is sticky-bottom with visualViewport keyboard inset so the
 * input stays above the keyboard.
 */

interface PendingMessage {
  clientId: string;
  body: string;
  createdAt: string;
  failed: boolean;
}

/** Day-key the message belongs to, in the viewer's local timezone. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Short clock time for a per-message timestamp, e.g. "3:42 PM". The
 *  date separator above carries the day, so the bubble time stays
 *  intentionally compact. */
function formatMessageTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Today" / "Yesterday" / "Mon, May 19" — separator between days. */
function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return 'Today';
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

/**
 * Label shown under the user's own bubble when the message isn't yet
 * visible to the peer. 'pending' is the silent transient state — the
 * cascade is running but the sender doesn't need an in-bubble warning
 * for that (the bubble already looks "sent"). Only the post-cascade
 * outcomes ('flagged' / 'blocked') surface an annotation, because at
 * that point a human will need to do something about it.
 *
 * Returning null means "render no annotation."
 */
function statusAnnotation(moderationStatus: string): string | null {
  switch (moderationStatus) {
    case 'flagged':
      return 'held for review';
    case 'blocked':
      return 'blocked';
    case 'pending':
    default:
      return null;
  }
}

/** Build 2–3 tappable openers for a brand-new conversation. Tapping one
 *  drops it into the composer (never auto-sends) so a user staring at a
 *  blank box has a way in. The third opener intentionally names the
 *  difficulty of starting — on-brand for a loneliness product. */
function buildOpeners(tags: string[]): string[] {
  const tag = tags[0]?.replace(/-/g, ' ');
  if (tag) {
    return [
      `Hi! I saw we both like ${tag} — what got you into it?`,
      `Hey, how's your week going?`,
      `Honestly, it's a little hard to start. Hi.`,
    ];
  }
  return [
    `Hey — how's your week going?`,
    `Hi! What's one small thing that went okay today?`,
    `Honestly, it's a little hard to start. Hi.`,
  ];
}

/** Compact relative time for the peer's last activity, e.g. "3h ago".
 *  Server-truthful — derived from the last peer message we actually hold.
 *  Deliberately NOT a presence / "online now" signal. */
function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

// Composer draft + reply-target survive an accidental nav / tab kill /
// WebView teardown. Async chat invites long, in-progress messages; losing
// one to a stray back-swipe is the cruelest small failure for this brand.
// Scoped per conversation id in sessionStorage (cleared when the tab
// closes — we don't want drafts lingering forever on a shared device).
const DRAFT_PREFIX = 'la-draft:';
const REPLY_PREFIX = 'la-reply:';
type ReplyTarget = { id: string; body: string; mine: boolean };
function loadDraft(id: string): string {
  try {
    return sessionStorage.getItem(DRAFT_PREFIX + id) ?? '';
  } catch {
    return '';
  }
}
function loadReply(id: string): ReplyTarget | null {
  try {
    const raw = sessionStorage.getItem(REPLY_PREFIX + id);
    return raw ? (JSON.parse(raw) as ReplyTarget) : null;
  } catch {
    return null;
  }
}

export function Conversation({ me }: { me: MeResponse }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The match link passes ?community=<id> so a member of multiple
  // communities lands in the right one. Falls back to memberships[0]
  // for deep links that don't carry the query (e.g. older push tokens).
  // We validate the query matches one of the user's memberships — if
  // not, ignore it; otherwise a stale link could try to load somebody
  // else's community and 404.
  const requestedCommunityId = searchParams.get('community');
  const communityId =
    requestedCommunityId && me.memberships.some((m) => m.communityId === requestedCommunityId)
      ? requestedCommunityId
      : (me.memberships[0]?.communityId ?? '');

  const [conv, setConv] = useState<ConversationDetail | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [draft, setDraft] = useState(() => loadDraft(id));
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [selfCrisis, setSelfCrisis] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [agreementPending, setAgreementPending] = useState<{
    body: string;
    clientId: string;
  } | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  // Fixed-position anchor for the reaction picker. Computed when the
  // long-press fires from the bubble's bounding rect, so the picker can
  // render via a portal at document.body and sit ABOVE every other
  // surface (the empty-state banner, the chat header, the messages list
  // overflow boundary). Without this the picker is clipped behind the
  // surrounding chrome and the user can't tap Copy / Reply / React.
  const [pickerAnchor, setPickerAnchor] = useState<{
    top: number;
    left: number;
    width: number;
    side: 'above' | 'below';
    align: 'start' | 'end';
  } | null>(null);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(() => loadReply(id));
  const endRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const emojiWrapRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const holdTimerRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);
  const movedRef = useRef(false);
  // Double-tap a bubble to ❤️ it (iMessage-style quick tapback). Tracks the
  // last clean tap so a second tap on the same message within ~320ms toggles
  // the heart. Long-press (reaction picker) and swipe (reply) are unaffected.
  const lastTapRef = useRef<{ id: string; t: number } | null>(null);
  // Live state for the in-progress swipe so the bubble translates with the
  // user's finger and a reply-arrow icon emerges from the gutter. Without
  // this the swipe-to-reply gesture is invisible — the bubble doesn't move
  // until release, so users have no idea the gesture exists. Mirrors the
  // iMessage / WhatsApp / Telegram pattern.
  const [swipeState, setSwipeState] = useState<{
    id: string;
    dx: number;
    crossed: boolean;
  } | null>(null);
  const SWIPE_THRESHOLD = 70;
  // Maximum visible drag (rubber-band past the threshold so users feel a
  // soft wall — they can pull further, but progressively less).
  const SWIPE_MAX = 110;

  function startHold(messageId: string) {
    heldRef.current = false;
    if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
    // 700 ms gives iOS Safari's native long-press text selection
    // (~500 ms) clear priority. The previous 350 ms intercepted the
    // gesture before iOS could surface its Copy / Select All menu, so
    // users could not copy a peer's message. If the user actually
    // wants the reaction picker, they can tap the explicit smiley
    // affordance on the message row.
    holdTimerRef.current = window.setTimeout(() => {
      heldRef.current = true;
      openReactionPicker(messageId);
    }, 700);
  }

  // Anchor the reaction picker to the bubble's bounding rect AND fire a
  // medium haptic the moment it appears. The picker renders via a portal
  // at document.body (see render below) so it isn't subject to any
  // ancestor overflow / transform / z-index — it sits cleanly above
  // every other surface on the page.
  function openReactionPicker(messageId: string) {
    const bubble = document.querySelector<HTMLElement>(`#msg-${messageId} .msg`);
    if (!bubble) {
      // Fall back to a centered position if we can't find the bubble for
      // some reason — the picker still works, just won't be anchored to
      // the bubble visually.
      setReactionPickerFor(messageId);
      setPickerAnchor(null);
      return;
    }
    const rect = bubble.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    // Reserve enough space above for the picker (~120px includes the
    // actions row + emoji row + padding). If there's no room above, the
    // picker drops below the bubble instead.
    const pickerH = 130;
    const side: 'above' | 'below' = rect.top - pickerH > 16 ? 'above' : 'below';
    const top = side === 'above' ? rect.top - 8 : rect.bottom + 8;
    const isMine = bubble.classList.contains('mine');
    const align: 'start' | 'end' = isMine ? 'end' : 'start';
    const left = align === 'start' ? rect.left : rect.right;
    // Clamp picker width to a comfortable 18rem (~288px) max so it never
    // stretches edge-to-edge on a phone.
    const width = Math.min(288, window.innerWidth - 24);
    setReactionPickerFor(messageId);
    setPickerAnchor({ top, left, width, side, align });
    // Native haptic so the picker appearing feels physical, not visual.
    void hapticTap('medium');
    // Belt: prevent the viewport from scrolling under the user's finger
    // while the picker is up — also stops the iOS rubber-band leak.
    void viewportH;
  }

  function cancelHold() {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }

  function onMessagePointerDown(event: React.PointerEvent<HTMLDivElement>, m: ChatMessage): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY };
    swipedRef.current = false;
    movedRef.current = false;
    startHold(m.id);
  }

  function onMessagePointerMove(event: React.PointerEvent<HTMLDivElement>, m: ChatMessage): void {
    const start = swipeStartRef.current;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Cancel the hold if the user moves at all — that's a swipe, not a press.
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      movedRef.current = true;
      cancelHold();
    }
    // Direction depends on which side of the chat the bubble lives:
    // peer bubbles (left side) swipe RIGHT to reveal a reply arrow in the
    // left gutter; own bubbles (right side) swipe LEFT to reveal one in
    // the right gutter. Vertical motion past ~24px cancels — it's a
    // scroll, not a swipe.
    if (Math.abs(dy) > Math.abs(dx) + 6 || Math.abs(dy) > 36) {
      // The user is scrolling, not swiping. Drop any in-progress visual.
      if (swipeState) setSwipeState(null);
      return;
    }
    const directional = m.mine ? Math.min(0, dx) : Math.max(0, dx);
    if (Math.abs(directional) < 6) {
      // Below noise floor — don't render a transform for taps.
      if (swipeState?.id === m.id) setSwipeState(null);
      return;
    }
    // Rubber-band past the threshold so dragging further feels like
    // resistance, not unbounded translation.
    const abs = Math.abs(directional);
    const eased =
      abs <= SWIPE_THRESHOLD
        ? abs
        : SWIPE_THRESHOLD +
          (SWIPE_MAX - SWIPE_THRESHOLD) * (1 - Math.exp(-(abs - SWIPE_THRESHOLD) / 60));
    const renderedDx = (directional < 0 ? -1 : 1) * eased;
    const crossed = abs >= SWIPE_THRESHOLD;

    // Fire a single haptic the moment we cross the threshold.
    if (crossed && (!swipeState || !swipeState.crossed || swipeState.id !== m.id)) {
      void hapticTap('light');
    }
    setSwipeState({ id: m.id, dx: renderedDx, crossed });

    // Commit the reply target the moment we cross the threshold so even
    // a finger-still-down state is "armed" — release commits, release
    // before threshold bounces back without committing.
    if (crossed && !swipedRef.current) {
      swipedRef.current = true;
      const trimmed = m.body.length > 80 ? `${m.body.slice(0, 80)}…` : m.body;
      setReplyTarget({ id: m.id, body: trimmed, mine: m.mine });
    }
  }

  function onMessagePointerEnd(event: React.PointerEvent<HTMLDivElement>, m: ChatMessage): void {
    cancelHold();
    swipeStartRef.current = null;
    // Spring the bubble back. The transition on .msg handles the actual
    // tween; clearing the state to null sends it back to translateX(0).
    setSwipeState(null);

    // Double-tap a bubble to ❤️ it. Only on a clean pointerup tap — no
    // swipe-commit, no long-press menu, no real movement — and only when
    // the tap didn't land on an interactive child (reply icon / reply-quote
    // / links). pointerleave / pointercancel never count as a tap.
    const onControl = !!(event.target as HTMLElement | null)?.closest?.('button, a');
    const wasTap =
      event.type === 'pointerup' &&
      !movedRef.current &&
      !heldRef.current &&
      !swipedRef.current &&
      !onControl;
    swipedRef.current = false;
    if (!wasTap) {
      lastTapRef.current = null;
      return;
    }
    const now = Date.now();
    const last = lastTapRef.current;
    if (last && last.id === m.id && now - last.t < 320) {
      lastTapRef.current = null;
      void hapticTap('medium');
      void toggleReaction(m.id, '❤️');
    } else {
      lastTapRef.current = { id: m.id, t: now };
    }
  }

  const refresh = useCallback(async () => {
    if (!communityId || !id) return;
    try {
      const detail = await fetchConversation(communityId, id);
      if (!detail) {
        setNotFound(true);
        return;
      }
      setConv(detail);
      const fresh = await fetchMessages(communityId, id);
      setMessages(fresh);
      // Backup dedup for the polling path (the primary path removes pending
      // entries by clientId in dispatchSend). Tolerate up to 30s of
      // client/server clock skew either direction so a strict
      // m.createdAt >= p.createdAt string comparison can't strand a pending
      // copy alongside its server-confirmed twin.
      setPending((prev) =>
        prev.filter(
          (p) =>
            p.failed ||
            !fresh.some(
              (m) =>
                m.mine &&
                m.body === p.body &&
                Math.abs(new Date(m.createdAt).getTime() - new Date(p.createdAt).getTime()) <
                  30_000,
            ),
        ),
      );
    } catch {
      // Keep the last good state; the next poll will retry.
    } finally {
      setLoaded(true);
    }
  }, [communityId, id]);

  // Show a "[peer] is replying…" indicator ONLY when the peer is an AI
  // persona (we know the typing window: the AI's 5–12 s read-then-
  // generate-then-send). For a real human peer we can't actually know
  // they're typing without a presence signal, and showing a fake
  // indicator would be misleading (and dishonest).
  //
  // Honesty window: the server schedules the AI's setTimeout for
  // 5–12 s after the user's message clears moderation. Showing the
  // indicator INSTANTLY on send (the previous behavior) was a lie —
  // the AI hasn't even been scheduled to start typing yet. The fix:
  //   * don't show it while the message is optimistic-pending;
  //   * don't show it for the first ~4 seconds after the message
  //     server-lands (matches the AI's actual "still reading" delay);
  //   * then show it for the next ~15 s window (covers the back end
  //     of the setTimeout jitter + Gemini latency);
  //   * clear immediately when the peer's reply arrives.
  // The 4–19 s window is a deliberate underestimate of the real window
  // — better to flash on briefly than to lie about typing instantly.
  const peerIsResponding =
    conv?.peerIsAi === true &&
    (() => {
      // Only count messages the peer can actually see — a pending (still
      // being moderated) message hasn't reached the AI yet, so the AI
      // is not "replying" to anything.
      const ownVisible = messages.filter((m) => m.mine && m.visibleToPeer);
      if (ownVisible.length === 0) return false;
      const latestMine = ownVisible.reduce(
        (max, m) => Math.max(max, new Date(m.createdAt).getTime()),
        0,
      );
      const latestAny = messages.reduce(
        (max, m) => Math.max(max, new Date(m.createdAt).getTime()),
        0,
      );
      // Peer has already replied → indicator off.
      if (latestMine !== latestAny) return false;
      const sinceMine = Date.now() - latestMine;
      // "Still reading" silence before the AI realistically starts.
      if (sinceMine < 4_000) return false;
      // Open window: cover the AI's 5–12 s schedule + Gemini ~2–3 s.
      return sinceMine < 19_000;
    })();

  // For each message, whether the day-separator label should render
  // BEFORE its bubble. True when the message's day differs from the
  // previous (visible) message's day — typically the very first message
  // and the first message of each subsequent day.
  const messageDaySeparators = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    let lastDay: string | null = null;
    for (const m of messages) {
      const day = dayKey(m.createdAt);
      if (day !== lastDay) {
        map.set(m.id, formatDayLabel(m.createdAt));
        lastDay = day;
      }
    }
    return map;
  }, [messages]);

  // Correspondence rhythm: consecutive messages from the same writer within
  // a short window cluster into a visual group — tighter spacing, facing
  // corners flattened, and only the last bubble carries the timestamp. A
  // day separator, a system prompt, or a reaction row breaks the group so
  // chips and date-marks always keep their breathing room.
  const messageGroupPos = useMemo<Map<string, 'solo' | 'first' | 'mid' | 'last'>>(() => {
    const pos = new Map<string, 'solo' | 'first' | 'mid' | 'last'>();
    const WINDOW_MS = 5 * 60 * 1000;
    const joins = (a: (typeof messages)[number], b: (typeof messages)[number]) =>
      a.kind !== 'system_prompt' &&
      b.kind !== 'system_prompt' &&
      a.mine === b.mine &&
      a.reactions.length === 0 &&
      !messageDaySeparators.has(b.id) &&
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() <= WINDOW_MS;
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i]!;
      if (m.kind === 'system_prompt') continue;
      const prev = i > 0 ? messages[i - 1]! : null;
      const next = i < messages.length - 1 ? messages[i + 1]! : null;
      const joinsPrev = prev !== null && joins(prev, m);
      const joinsNext = next !== null && joins(m, next);
      pos.set(m.id, joinsPrev ? (joinsNext ? 'mid' : 'last') : joinsNext ? 'first' : 'solo');
    }
    return pos;
  }, [messages, messageDaySeparators]);

  // The peer's most recent message time — drives a calm "Last replied Xh
  // ago" line under the header. Derived from messages we already hold, so
  // no API change and no presence / online signal.
  const peerLastWroteAt = useMemo<string | null>(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]!;
      if (!m.mine && m.kind === 'peer') return m.createdAt;
    }
    return null;
  }, [messages]);

  // Any of the user's own messages still being checked by the cascade?
  // We poll faster while at least one is, so the "checking…" annotation
  // disappears within ~1s of the verdict instead of waiting for the next
  // 5s tick. Self-stabilising: once the message clears (or is flagged),
  // hasPending flips back to false and the interval relaxes to 5s.
  //
  // Cap the fast window at 60s so a message stuck in 'pending' (cascade
  // timeout, queue backlog) can't pin the client to 1s polling forever —
  // real battery + shared-DB load on a multi-tenant pilot.
  const hasPending = messages.some(
    (m) =>
      m.mine &&
      m.moderationStatus === 'pending' &&
      Date.now() - new Date(m.createdAt).getTime() < 60_000,
  );

  // Async chat: poll for new messages rather than holding a live socket.
  // The interval is paused while the tab/page is hidden — on mobile the
  // OS backgrounding the browser was previously still firing the
  // 1s-or-5s poll, which burned cellular data and battery for nothing
  // the user could see. document.visibilityState comes back online via
  // the visibilitychange listener and we resume immediately (plus do a
  // fresh refresh so the user sees the latest the moment they return).
  useEffect(() => {
    let timer: number | null = null;

    const start = () => {
      if (timer !== null) return;
      const interval = hasPending ? 1000 : 5000;
      timer = window.setInterval(() => void refresh(), interval);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
        start();
      } else {
        stop();
      }
    };

    void refresh();
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, hasPending]);

  // Mobile keyboard inset — keeps the composer above the on-screen keyboard.
  // visualViewport.height shrinks when the keyboard opens; the difference
  // from window.innerHeight is what we need to pad-bottom the page.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--keyboard-inset', `${inset}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--keyboard-inset');
    };
  }, []);

  // Auto-scroll to the latest message.
  //
  // Two behaviors share this hook:
  //   * On first paint of a loaded thread, jump to the bottom
  //     unconditionally — opening a conversation should always land
  //     on the most recent message, never at the very top with an
  //     hour of backscroll between the user and what they just came
  //     to read.
  //   * On subsequent polls, only scroll if the user is already near
  //     the bottom (within ~120 px). Otherwise the polling refresh
  //     would yank them mid-conversation every few seconds.
  //
  // The didFirstScrollRef flag is what separates "user just opened
  // this chat" from "polling tick on an already-open chat." It flips
  // true the first time we actually scroll on a non-empty message
  // array.
  //
  // Drive the scroll by assigning scrollTop directly on the messages
  // container instead of node.scrollIntoView(). On iOS Safari with the
  // soft keyboard open, scrollIntoView walks ancestors and ends up
  // scrolling the layout viewport too — the page jumps and the
  // keyboard then occludes the visible chat. Direct scrollTop is
  // confined to the single overflow:auto container.
  const didFirstScrollRef = useRef(false);
  // Reset the "did we jump to bottom yet?" flag whenever the active
  // conversation id changes. Otherwise navigating from one chat into
  // another (without an unmount) would skip the auto-jump on the
  // second chat — the ref would still be true from the first one.
  useEffect(() => {
    didFirstScrollRef.current = false;
  }, [id]);

  // Restore any saved draft / reply-target when the active conversation
  // changes (covers in-place navigation between chats without an unmount).
  useEffect(() => {
    setDraft(loadDraft(id));
    setReplyTarget(loadReply(id));
  }, [id]);

  // Persist the draft as the user types so a teardown can't eat it.
  useEffect(() => {
    try {
      if (draft) sessionStorage.setItem(DRAFT_PREFIX + id, draft);
      else sessionStorage.removeItem(DRAFT_PREFIX + id);
    } catch {
      // sessionStorage unavailable (private mode) — in-memory still works.
    }
  }, [id, draft]);

  // Persist the reply target the same way.
  useEffect(() => {
    try {
      if (replyTarget) sessionStorage.setItem(REPLY_PREFIX + id, JSON.stringify(replyTarget));
      else sessionStorage.removeItem(REPLY_PREFIX + id);
    } catch {
      // ignore
    }
  }, [id, replyTarget]);

  // Count of peer messages that arrived while the user was NOT near
  // the bottom of the scroller. Drives the "↓ New message" floating
  // pill: surfaces an explicit nudge instead of silently leaving the
  // user scrolled up, which used to mean they'd never know a reply
  // had landed unless they scrolled down by hand.
  const [unreadBelow, setUnreadBelow] = useState(0);
  // Compared against on every scroll event to detect "user has now
  // crossed back into the near-bottom region" — when that happens, the
  // pill clears even if they didn't tap it.
  const lastSeenMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    const node = endRef.current;
    if (!node) return;
    const scroller = node.parentElement; // the .messages container
    if (!scroller) return;
    if (messages.length === 0 && pending.length === 0) return;

    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const nearBottom = distanceFromBottom < 120;

    if (!didFirstScrollRef.current) {
      scroller.scrollTop = scroller.scrollHeight;
      didFirstScrollRef.current = true;
      lastSeenMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      setUnreadBelow(0);
      return;
    }

    if (nearBottom) {
      scroller.scrollTop = scroller.scrollHeight;
      lastSeenMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      setUnreadBelow(0);
      return;
    }

    // User is scrolled up. Count any new peer messages that landed
    // since the last seen marker — these are the ones the user has
    // not seen yet. (Own messages don't count; they just sent it.)
    const lastSeen = lastSeenMessageIdRef.current;
    if (lastSeen == null) {
      lastSeenMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      return;
    }
    let seenIndex = messages.findIndex((m) => m.id === lastSeen);
    if (seenIndex === -1) seenIndex = -1; // treat all as new
    let unread = 0;
    for (let i = seenIndex + 1; i < messages.length; i += 1) {
      const m = messages[i]!;
      if (!m.mine && m.kind === 'peer') unread += 1;
    }
    setUnreadBelow(unread);
  }, [messages, pending]);

  function jumpToBottom() {
    const node = endRef.current;
    const scroller = node?.parentElement;
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
    lastSeenMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
    setUnreadBelow(0);
  }

  // Clear the unread counter when the user organically scrolls back
  // to the bottom (without tapping the pill). Throttled implicitly by
  // the browser's scroll event cadence.
  useEffect(() => {
    const node = endRef.current;
    const scroller = node?.parentElement;
    if (!scroller) return;
    function onScroll() {
      if (!scroller) return;
      const dist = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (dist < 80) {
        lastSeenMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
        setUnreadBelow(0);
      }
    }
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [messages]);

  async function dispatchSend(body: string, clientId: string, replyToId?: string) {
    const result = await sendMessage(communityId, id, body, replyToId);
    if (result.ok) {
      if (result.crisis) setSelfCrisis(true);
      // Remove the optimistic entry by exact clientId match the moment the
      // server confirms. The refresh() dedup is a backup for the polling
      // path; relying on it alone (body + timestamp match) left pending
      // entries stranded next to their confirmed twins when there was any
      // client/server clock skew.
      setPending((prev) => prev.filter((p) => p.clientId !== clientId));
      await refresh();
      return;
    }
    if (result.error === 'agreement_required') {
      // Hold the optimistic message in place and open the modal. After the
      // user agrees we re-dispatch the same body + clientId (replyToId is
      // not preserved across the modal — agreement is the first message).
      setAgreementPending({ body, clientId });
      return;
    }
    setPending((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, failed: true } : p)));
    setError(result.error);
  }

  async function onAgree() {
    const accepted = await acceptPairingAgreement(communityId, id);
    if (!accepted.ok) throw new Error(accepted.error);
    const pendingItem = agreementPending;
    setAgreementPending(null);
    if (pendingItem) {
      await dispatchSend(pendingItem.body, pendingItem.clientId);
    }
  }

  function onAgreementCancel() {
    // Drop the optimistic copy — the user backed out of the agreement.
    if (agreementPending) discard(agreementPending.clientId);
    setAgreementPending(null);
  }

  function onSend(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const clientId = `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const replyToId = replyTarget?.id;
    // Show the message immediately — the network round-trip + moderation
    // cascade happen in the background.
    setPending((prev) => [
      ...prev,
      { clientId, body: text, createdAt: new Date().toISOString(), failed: false },
    ]);
    setDraft('');
    setReplyTarget(null);
    setError(null);
    // Light haptic on send — only fires on native, where the OS keyboard's
    // own click tone is muted by default. Makes the action feel committed.
    void hapticTap('light');
    // Keep focus in the composer so back-to-back sends feel snappy.
    composerRef.current?.focus();
    void dispatchSend(text, clientId, replyToId);
  }

  function scrollToMessage(messageId: string) {
    const el = document.getElementById(`msg-${messageId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Brief highlight pulse so the user sees which message they jumped to —
    // a quoted line scrolled into the middle of a long thread is otherwise
    // hard to pick out.
    el.classList.add('msg-row--flash');
    window.setTimeout(() => el.classList.remove('msg-row--flash'), 1200);
  }

  function retry(clientId: string) {
    const item = pending.find((p) => p.clientId === clientId);
    if (!item) return;
    setPending((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, failed: false } : p)));
    setError(null);
    void dispatchSend(item.body, clientId);
  }

  function discard(clientId: string) {
    setPending((prev) => prev.filter((p) => p.clientId !== clientId));
  }

  function insertEmoji(emoji: string) {
    const ta = composerRef.current;
    if (!ta) {
      setDraft((prev) => prev + emoji);
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const next = ta.value.slice(0, start) + emoji + ta.value.slice(end);
    setDraft(next);
    // Restore the caret just after the inserted glyph on the next paint —
    // setDraft hasn't flushed yet, so we wait a tick.
    requestAnimationFrame(() => {
      const caret = start + emoji.length;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  // Close the emoji popover on outside click or Escape.
  useEffect(() => {
    if (!emojiOpen) return;
    const onPointer = (event: PointerEvent) => {
      if (emojiWrapRef.current?.contains(event.target as Node)) return;
      setEmojiOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEmojiOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [emojiOpen]);

  // Close the header overflow menu on outside click or Escape.
  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onPointer = (event: PointerEvent) => {
      if (actionsMenuRef.current?.contains(event.target as Node)) return;
      setActionsMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActionsMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [actionsMenuOpen]);

  // The web component fires 'emoji-click' with detail.unicode — there is no
  // React synthetic event for custom-element events, so we wire it via the
  // DOM ref. Re-run when the popover (re-)mounts.
  useEffect(() => {
    if (!emojiOpen) return;
    const picker = emojiPickerRef.current;
    if (!picker) return;
    const onClick = (event: Event) => {
      const detail = (event as CustomEvent<{ unicode?: string }>).detail;
      if (detail?.unicode) {
        insertEmoji(detail.unicode);
        setEmojiOpen(false);
      }
    };
    picker.addEventListener('emoji-click', onClick);
    return () => picker.removeEventListener('emoji-click', onClick);
  }, [emojiOpen]);

  // Close the reaction picker on Escape (the scrim under the portal-
  // rendered picker handles outside-tap dismissal). We no longer need
  // the document-wide pointerdown listener because the scrim covers
  // every non-picker pixel.
  useEffect(() => {
    if (!reactionPickerFor) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setReactionPickerFor(null);
        setPickerAnchor(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [reactionPickerFor]);

  /**
   * Copy a message body to the OS clipboard. Uses the modern async
   * clipboard API where available; falls back to a hidden textarea +
   * execCommand on older mobile browsers (iOS < 13.4, in-app webviews
   * that lock down navigator.clipboard). Best-effort: a failed copy
   * is silent — the user can long-press the text to use native select
   * if our copy path is blocked.
   */
  async function copyMessageBody(body: string) {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(body);
        return;
      }
    } catch {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = body;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch {
      // give up — long-press text-select still works on every browser
    }
  }

  /**
   * Optimistic toggle: update the local message immediately, then call the
   * server. The next 5s poll will replace the optimistic state with the
   * server's truth (and correct it if the request failed).
   */
  async function toggleReaction(messageId: string, emoji: string, mine?: boolean) {
    const target = messages.find((m) => m.id === messageId);
    if (!target) return;
    const known = target.reactions.find((r) => r.emoji === emoji);
    const wasMine = mine ?? known?.mine ?? false;

    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const existing = m.reactions.find((r) => r.emoji === emoji);
        let next: MessageReaction[];
        if (wasMine && existing) {
          const reduced = existing.count - 1;
          next =
            reduced > 0
              ? m.reactions.map((r) =>
                  r.emoji === emoji ? { ...r, count: reduced, mine: false } : r,
                )
              : m.reactions.filter((r) => r.emoji !== emoji);
        } else if (existing) {
          next = m.reactions.map((r) =>
            r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r,
          );
        } else {
          next = [...m.reactions, { emoji, count: 1, mine: true }];
        }
        return { ...m, reactions: next };
      }),
    );

    const result = wasMine
      ? await removeReaction(communityId, id, messageId, emoji)
      : await addReaction(communityId, id, messageId, emoji);

    if (result.ok) {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, reactions: result.reactions } : m)),
      );
    } else {
      // The next poll will reconcile; surface the error inline.
      setError(result.error);
    }
  }

  if (!loaded) {
    return (
      <main className="chat-page">
        <div className="card chat-card">
          <p>Loading…</p>
        </div>
      </main>
    );
  }

  if (notFound || !conv) {
    return (
      <main className="chat-page">
        <div className="card chat-card">
          <p>Conversation not found.</p>
          <p>
            <Link to="/">← Back</Link>
          </p>
        </div>
      </main>
    );
  }

  const active = conv.status === 'active';
  // statusNote only fires for `paused` now — the ended branch is
  // handled by <GoodbyeLetter> rendered next to the composer slot.
  const statusNote =
    conv.status === 'paused' ? 'This conversation is paused while a moderator reviews it.' : null;
  const showSelfCrisis = selfCrisis || messages.some((m) => m.mine && m.crisisDetected);
  const showPeerCrisis = messages.some((m) => !m.mine && m.crisisDetected);

  return (
    <main className="chat-page">
      <div className="card chat-card">
        <p className="step-label">
          <Link to="/">← All matches</Link>
        </p>
        <div className="chat-header-row">
          {/* A small "wax seal" with the peer's initial — gives the
              correspondent a presence without an avatar (nicknames only;
              pseudonymity intact). Pure decoration for screen readers. */}
          <span className="chat-seal" aria-hidden="true">
            {conv.peerNickname.charAt(0).toUpperCase()}
          </span>
          <div className="chat-letterhead">
            <span className="chat-letterhead-eyebrow">Writing to</span>
            <h1 className="chat-letterhead-name">{conv.peerNickname}</h1>
            {active && (
              <span className="chat-letterhead-meta">
                {peerLastWroteAt
                  ? `Last replied ${formatRelativeTime(peerLastWroteAt)}`
                  : messages.some((m) => m.mine)
                    ? 'No reply yet — slow is normal'
                    : 'No messages yet'}
              </span>
            )}
          </div>
          <div className="chat-actions-menu" ref={actionsMenuRef}>
            <button
              type="button"
              className="chat-actions-trigger"
              aria-label="Conversation actions"
              aria-haspopup="menu"
              aria-expanded={actionsMenuOpen}
              onClick={() => setActionsMenuOpen((open) => !open)}
            >
              <span aria-hidden="true">⋯</span>
            </button>
            {actionsMenuOpen && (
              <div className="chat-actions-dropdown" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActionsMenuOpen(false);
                    setReportOpen(true);
                  }}
                >
                  Report this conversation
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActionsMenuOpen(false);
                    setBlockOpen(true);
                  }}
                >
                  Block &amp; end
                </button>
              </div>
            )}
          </div>
        </div>
        {statusNote && <p className="muted">{statusNote}</p>}
        {conv.sharedTags.length > 0 && (
          <div className="shared-tags">
            <span className="shared-tags-label">You both like:</span>
            {conv.sharedTags.map((tag) => (
              <span key={tag} className="chip">
                {tag.replace(/-/g, ' ')}
              </span>
            ))}
          </div>
        )}

        {showSelfCrisis && (
          <div className="crisis">
            <strong>You don’t have to go through this alone.</strong>
            <p>
              It sounds like things are really hard right now. These lines are free, confidential,
              and open any time:
            </p>
            <CrisisResources />
          </div>
        )}
        {showPeerCrisis && (
          <div className="crisis">
            <strong>Your match shared something difficult.</strong>
            <p>
              You don’t have to fix it or carry it — that isn’t your role. Listening helps; the most
              caring thing you can do is gently point them to real support:
            </p>
            <CrisisResources />
          </div>
        )}

        <div className="messages">
          {messages.length === 0 && pending.length === 0 && (
            <div className="empty-chat">
              <span className="empty-chat-mark" aria-hidden="true">
                ✦ ✦ ✦
              </span>
              <p className="empty-chat-title">You're matched with {conv.peerNickname}.</p>
              {conv.sharedTags.length > 0 ? (
                <p className="muted">
                  You both like{' '}
                  {conv.sharedTags.map((tag, i) => (
                    <span key={tag}>
                      <strong>{tag.replace(/-/g, ' ')}</strong>
                      {i < conv.sharedTags.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                  . No pressure to be clever — a small hello is plenty.
                </p>
              ) : (
                <p className="muted">
                  A first message that lands well is usually short, honest, and not heavy.
                </p>
              )}
              <div className="opener-chips" role="group" aria-label="Conversation openers">
                {buildOpeners(conv.sharedTags).map((opener) => (
                  <button
                    key={opener}
                    type="button"
                    className="opener-chip"
                    onClick={() => {
                      setDraft(opener);
                      composerRef.current?.focus();
                    }}
                  >
                    {opener}
                  </button>
                ))}
              </div>
              <p className="muted empty-chat-tip">
                Tap one to start, or write your own. LessAlone is asynchronous — they might reply
                right away, or in a day or two. A slow reply isn't rejection.
              </p>
            </div>
          )}
          {messages.map((m) => {
            const dayLabel = messageDaySeparators.get(m.id);
            const separator = dayLabel ? (
              <div key={`sep-${m.id}`} className="day-separator" aria-hidden="false">
                <span>{dayLabel}</span>
              </div>
            ) : null;
            if (m.kind === 'system_prompt') {
              return (
                <Fragment key={m.id}>
                  {separator}
                  <div className="msg system-prompt">
                    <span className="system-prompt-eyebrow">A conversation starter</span>
                    <span className="system-prompt-body">{m.body}</span>
                  </div>
                </Fragment>
              );
            }
            const activeSwipe = swipeState?.id === m.id ? swipeState : null;
            // Reveal-arrow opacity tracks the drag distance — barely visible
            // at first, fully solid once we cross the reply threshold.
            const swipeProgress = activeSwipe
              ? Math.min(1, Math.abs(activeSwipe.dx) / SWIPE_THRESHOLD)
              : 0;
            const grp = messageGroupPos.get(m.id) ?? 'solo';
            // A held/blocked note is a page still on the desk — wash +
            // dashed edge instead of the full sage "delivered" bubble.
            const held =
              m.mine &&
              !m.visibleToPeer &&
              (m.moderationStatus === 'blocked' || m.moderationStatus === 'flagged');
            return (
              <Fragment key={m.id}>
                {separator}
                <div
                  id={`msg-${m.id}`}
                  className={`msg-row${activeSwipe ? ' swiping' : ''}${activeSwipe?.crossed ? ' swipe-armed' : ''}${grp === 'mid' || grp === 'last' ? ' grp-cont' : ''}`}
                >
                  {activeSwipe && (
                    <span
                      className="msg-swipe-hint"
                      aria-hidden="true"
                      style={{ opacity: swipeProgress }}
                    >
                      ↩
                    </span>
                  )}
                  <div
                    className={`msg ${m.mine ? 'mine' : 'peer'} grp-${grp}${held ? ' held' : ''}`}
                    style={
                      activeSwipe
                        ? { transform: `translateX(${activeSwipe.dx}px)`, transition: 'none' }
                        : undefined
                    }
                    onPointerDown={(event) => onMessagePointerDown(event, m)}
                    onPointerMove={(event) => onMessagePointerMove(event, m)}
                    onPointerUp={(event) => onMessagePointerEnd(event, m)}
                    onPointerLeave={(event) => onMessagePointerEnd(event, m)}
                    onPointerCancel={(event) => onMessagePointerEnd(event, m)}
                  >
                    {m.replyTo && (
                      <button
                        type="button"
                        className="reply-quote"
                        onClick={() => scrollToMessage(m.replyTo!.id)}
                        aria-label="Jump to the quoted message"
                      >
                        <span className="reply-quote-who">
                          {m.replyTo.mineQuoted ? 'You wrote' : `${conv.peerNickname} wrote`}
                        </span>
                        <span className="reply-quote-body">↪ {m.replyTo.body}</span>
                      </button>
                    )}
                    {m.body}
                    {(() => {
                      // Moderation-status annotation wins for the user's own
                      // still-being-checked messages — telling them the
                      // message is in flight is more useful than the wall
                      // time. Otherwise show the clock time so a reader can
                      // see when each message landed (date is carried by
                      // the separator above). Within a group, only the final
                      // bubble carries the time — the cluster shares one
                      // moment; annotations always render regardless.
                      const annotation =
                        m.mine && !m.visibleToPeer ? statusAnnotation(m.moderationStatus) : null;
                      if (annotation === null && (grp === 'first' || grp === 'mid')) return null;
                      const label = annotation ?? formatMessageTime(m.createdAt);
                      return <span className="msg-time">{label}</span>;
                    })()}
                    {m.mine &&
                      !m.visibleToPeer &&
                      (m.moderationStatus === 'blocked' || m.moderationStatus === 'flagged') && (
                        <span className="msg-held-note">
                          {m.moderationStatus === 'blocked'
                            ? 'Only you can see this — our safety filter held it back. Try rewording it, or send something else.'
                            : 'Only you can see this yet — a moderator usually takes a look within an hour.'}
                        </span>
                      )}
                    <button
                      type="button"
                      className="msg-reply-icon"
                      aria-label="Reply to this message"
                      onClick={(event) => {
                        event.stopPropagation();
                        const trimmed = m.body.length > 80 ? `${m.body.slice(0, 80)}…` : m.body;
                        setReplyTarget({ id: m.id, body: trimmed, mine: m.mine });
                        composerRef.current?.focus();
                      }}
                    >
                      <span aria-hidden="true">↩</span>
                    </button>
                    {reactionPickerFor === m.id &&
                      pickerAnchor &&
                      createPortal(
                        <>
                          <div
                            className="reaction-picker-scrim"
                            onPointerDown={() => {
                              setReactionPickerFor(null);
                              setPickerAnchor(null);
                            }}
                            aria-hidden="true"
                          />
                          <div
                            className={`reaction-picker reaction-picker--${pickerAnchor.side} reaction-picker--align-${pickerAnchor.align}`}
                            role="dialog"
                            aria-label="Message actions"
                            ref={reactionPickerRef}
                            style={{
                              position: 'fixed',
                              top: pickerAnchor.top,
                              [pickerAnchor.align === 'start' ? 'left' : 'right']:
                                pickerAnchor.align === 'start'
                                  ? pickerAnchor.left
                                  : window.innerWidth - pickerAnchor.left,
                              width: pickerAnchor.width,
                              transform:
                                pickerAnchor.side === 'above'
                                  ? 'translateY(-100%)'
                                  : 'translateY(0)',
                              zIndex: 100,
                            }}
                          >
                            <div className="msg-actions-row">
                              <button
                                type="button"
                                className="msg-action"
                                onClick={() => {
                                  void copyMessageBody(m.body);
                                  setReactionPickerFor(null);
                                  setPickerAnchor(null);
                                }}
                              >
                                <span className="msg-action-glyph" aria-hidden="true">
                                  ⎘
                                </span>
                                <span>Copy</span>
                              </button>
                              <button
                                type="button"
                                className="msg-action"
                                onClick={() => {
                                  const trimmed =
                                    m.body.length > 80 ? `${m.body.slice(0, 80)}…` : m.body;
                                  setReplyTarget({ id: m.id, body: trimmed, mine: m.mine });
                                  composerRef.current?.focus();
                                  setReactionPickerFor(null);
                                  setPickerAnchor(null);
                                }}
                              >
                                <span className="msg-action-glyph" aria-hidden="true">
                                  ↩
                                </span>
                                <span>Reply</span>
                              </button>
                            </div>
                            <div className="msg-emoji-row">
                              {REACTION_EMOJIS.map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  className="reaction-pick"
                                  onClick={() => {
                                    void toggleReaction(m.id, emoji);
                                    setReactionPickerFor(null);
                                    setPickerAnchor(null);
                                  }}
                                >
                                  <span aria-hidden="true">{emoji}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>,
                        document.body,
                      )}
                  </div>
                  {m.reactions.length > 0 && (
                    <div className={`reactions ${m.mine ? 'reactions-mine' : 'reactions-peer'}`}>
                      {m.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          type="button"
                          className={`reaction-chip${r.mine ? ' on' : ''}`}
                          aria-pressed={r.mine}
                          onClick={() => void toggleReaction(m.id, r.emoji, r.mine)}
                        >
                          <span className="reaction-chip-emoji" aria-hidden="true">
                            {r.emoji}
                          </span>
                          <span className="reaction-chip-count">{r.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Fragment>
            );
          })}
          {pending.map((p) => (
            <div
              key={p.clientId}
              className={`msg mine pending${p.failed ? ' failed' : ''}`}
              aria-live="polite"
            >
              {p.body}
              <span className="msg-time">
                {p.failed ? 'didn’t send' : 'sending…'}
                {p.failed && (
                  <>
                    {' · '}
                    <button type="button" className="link" onClick={() => retry(p.clientId)}>
                      Retry
                    </button>
                    {' · '}
                    <button type="button" className="link" onClick={() => discard(p.clientId)}>
                      Discard
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
          {peerIsResponding && (
            <div className="peer-typing" aria-live="polite">
              <span className="peer-typing-dot" />
              <span className="peer-typing-dot" />
              <span className="peer-typing-dot" />
              <span className="peer-typing-label">{conv.peerNickname} is writing…</span>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {unreadBelow > 0 && (
          <button
            type="button"
            className="jump-pill"
            aria-label={`Jump to ${unreadBelow} new message${unreadBelow === 1 ? '' : 's'}`}
            onClick={jumpToBottom}
          >
            <span aria-hidden="true">↓</span>{' '}
            {unreadBelow === 1 ? 'New message' : `${unreadBelow} new messages`}
          </button>
        )}

        <div className="chat-footer">
          {/* Goodbye letter replaces the composer once the conversation
              has ended. Tone shifts with endReason — graduated/member_ended
              get a warm closing, abandoned gets a wistful one, ban /
              csam_banned / founder_action get a safety-flavoured note.
              See components/GoodbyeLetter.tsx. */}
          {!active && conv.status !== 'paused' && (
            <GoodbyeLetter
              peerNickname={conv.peerNickname}
              endReason={conv.endReason}
              endedAt={conv.endedAt}
            />
          )}
          {active && (
            <form onSubmit={onSend} className="composer">
              {replyTarget && (
                <div className="reply-strip">
                  <span className="reply-strip-label">
                    Replying to {replyTarget.mine ? 'yourself' : conv.peerNickname}:
                  </span>
                  <span className="reply-strip-body">{replyTarget.body}</span>
                  <button
                    type="button"
                    className="reply-strip-cancel"
                    aria-label="Cancel reply"
                    onClick={() => setReplyTarget(null)}
                  >
                    ✕
                  </button>
                </div>
              )}
              <textarea
                ref={composerRef}
                value={draft}
                maxLength={4000}
                enterKeyHint="send"
                placeholder="Write whenever you can — there's no rush."
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  // On a touch device the on-screen keyboard's only confirm
                  // gesture is Enter, so plain Enter sends and Shift+Enter is
                  // a newline. On desktop we keep Cmd/Ctrl+Enter to send and
                  // leave plain Enter as a newline for composing longer notes.
                  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
                  const send = event.metaKey || event.ctrlKey || (coarse && !event.shiftKey);
                  if (send) {
                    event.preventDefault();
                    onSend(event as unknown as FormEvent);
                  }
                }}
              />
              {draft.length > 3500 && (
                <span className="composer-counter">
                  {4000 - draft.length} characters left — you can always continue in another
                  message.
                </span>
              )}
              <div className="composer-actions">
                <div className="emoji-wrap" ref={emojiWrapRef}>
                  <button
                    type="button"
                    className="emoji-toggle"
                    aria-label="Insert emoji"
                    aria-expanded={emojiOpen}
                    aria-haspopup="dialog"
                    onClick={() => setEmojiOpen((v) => !v)}
                  >
                    <span aria-hidden="true">🙂</span>
                  </button>
                  {emojiOpen && (
                    <div className="emoji-popover" role="dialog" aria-label="Emoji picker">
                      <emoji-picker ref={emojiPickerRef} class="emoji-picker" />
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="composer-send"
                  aria-label="Send"
                  disabled={!draft.trim()}
                >
                  <span className="composer-send-icon" aria-hidden="true">
                    ↑
                  </span>
                  <span className="composer-send-label">Send</span>
                </button>
              </div>
            </form>
          )}
          {error && <p className="error">{error}</p>}

          <p className="note">
            LessAlone is not a crisis service and is not monitored in real time. In an emergency,
            call or text 988, or call 911.
          </p>
        </div>
      </div>
      {reportOpen && (
        <ReportModal
          onClose={() => setReportOpen(false)}
          onSubmit={async (reason) => {
            const result = await reportConversation(communityId, id, reason);
            return result;
          }}
        />
      )}
      {blockOpen && (
        <BlockModal
          onClose={() => setBlockOpen(false)}
          onSubmit={async () => {
            const result = await blockConversation(communityId, id);
            if (result.ok) navigate('/');
            return result;
          }}
        />
      )}
      {agreementPending && <PeerAgreementModal onAgree={onAgree} onCancel={onAgreementCancel} />}
    </main>
  );
}
